import { Box, Flex } from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import {
  useReviewCreationV1Data,
  setReviewCreationKey,
  useReviewCreationDerivedMetaData,
  setReviewCreationReviewerSetting,
} from 'reducers/reviewCreationV1Slice';

import { NumberInput } from 'uiToolkitV1/InputV2/NumberInput';
import { RadioSelect } from 'uiToolkitV1/RadioSelect';
import { SelectDashed } from 'uiToolkitV1/Select';
import { TooltipInfoV1 } from 'uiToolkitV1/TooltipInfoV1';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { AlertBar } from '../../ReviewProcessV1/components/AlertBar';
import { ReviewCreationSidepanel } from '../components/ReviewCreationSidepanel';
import {
  useGetFormsPanelData,
  useInvalidateFormsPanelData,
  useUpdateFormsPanelData,
} from '../dataHooks/formsSectionHooks';
import { PreviewFormButton } from '../sections/Forms';

const MAX_ALLOWED_NOMINATION_COUNT = 100;

const validateTotalMinMaxValues = ({ minPeers, maxPeers }) => {
  if (
    typeof minPeers !== 'number' ||
    typeof maxPeers !== 'number' ||
    Number.isNaN(minPeers) ||
    Number.isNaN(maxPeers) ||
    minPeers > maxPeers
  ) {
    return false;
  }
  return true;
};

const Header = () => (
  <Flex p="8px 12px 4px 2px" justifyContent="space-between" alignItems="center">
    <TextBodyMedium bold>Forms</TextBodyMedium>

    {/* <PreviewFormButton /> */}
  </Flex>
);

const CardHeader = () => (
  <Box flex={1} pb={2} borderBottom="1px solid" borderColor="border.CFD7DF">
    <TextBodyMedium bold py={2}>
      Nomination
    </TextBodyMedium>
  </Box>
);

// value is in context of related api data boolean isAllowPeerNominationBySelf
export const NOMINATOR_OPTIONS = [
  { label: 'Employee, Manager', value: true },
  { label: 'Manager', value: false },
];

const FormsSettingsPanel = () => {
  const dispatch = useDispatch();
  useGetFormsPanelData();

  const { reviewerSettings, isFormsSettingsPanelOpen } = useReviewCreationV1Data();

  const { hasPeerForm, hasDLMForm } = useReviewCreationDerivedMetaData();

  const invalidateFormsPanelData = useInvalidateFormsPanelData();

  const { mutate: updateFormsPanelData } = useUpdateFormsPanelData();
  // although right now in milestone 1 it is guaranteed that atleast one form will be there if sidepanel is opened,
  // I am still keeping this there because when we add other properties then sidepanel will be there even if peer and dlm form is not there
  const hasPeerDMLAnyForm = hasPeerForm || hasDLMForm;

  const { isAutoAcceptDlmNomination, isAutoAcceptPeerNomination, isAllowPeerNominationBySelf, minPeers, maxPeers } =
    reviewerSettings;

  const isValidTotalMinMax = validateTotalMinMaxValues({ minPeers, maxPeers });

  const handleClose = () => {
    dispatch(setReviewCreationKey({ isFormsSettingsPanelOpen: false }));
    invalidateFormsPanelData();
  };

  const onChangeNominatorOption = ({ value }) => {
    dispatch(setReviewCreationReviewerSetting({ isAllowPeerNominationBySelf: value }));
  };

  const onChangePropertyHandler = (newValue, propertyName) => {
    dispatch(setReviewCreationReviewerSetting({ [propertyName]: newValue }));
  };

  const handleSaveClick = () => {
    updateFormsPanelData();
  };

  return (
    <ReviewCreationSidepanel
      isOpen={isFormsSettingsPanelOpen}
      onClose={handleClose}
      onSave={handleSaveClick}
      isSaveDisabled={!isValidTotalMinMax}
    >
      <Flex py={2} flexDir="column" gap={2} mb={1}>
        <Header />
      </Flex>
      <Flex borderRadius="8px" flexDir="column" border="4px solid #E7EBEF" bg="white" pb={4}>
        <Box py={2} px={3}>
          <CardHeader />
        </Box>
        <Flex flexDir="column" py={2} px={3} gap={8}>
          {hasPeerDMLAnyForm && (
            <Flex alignItems="center" gap={3}>
              <Flex>
                <TextBodyMedium>Who can nominate reviewers?</TextBodyMedium>
                <TooltipInfoV1 title="Note that Admins can nominate any time." />
              </Flex>
              <Box flexGrow={1}>
                <SelectDashed
                  value={NOMINATOR_OPTIONS.find(({ value }) => isAllowPeerNominationBySelf === value)}
                  options={NOMINATOR_OPTIONS}
                  handleChange={onChangeNominatorOption}
                />
              </Box>
            </Flex>
          )}

          {hasPeerDMLAnyForm && (
            <Flex gap={2} flexDir="column">
              <TextBodyMedium>Enable auto-accept for nomination invites</TextBodyMedium>
              {hasPeerForm && (
                <Flex alignItems="center" pl={3} mb={1}>
                  <TextLabel minW="160px">Peers:</TextLabel>
                  <RadioSelect
                    containerProps={{ mt: '0' }}
                    optionsProps={{ minW: 'auto' }}
                    options={[{ name: 'Yes' }, { name: 'No' }]}
                    selectedIndex={isAutoAcceptPeerNomination ? 0 : 1}
                    onChange={(index) => onChangePropertyHandler(index === 0, 'isAutoAcceptPeerNomination')}
                  />
                </Flex>
              )}

              {hasDLMForm && (
                <Flex alignItems="center" pl={3}>
                  <TextLabel minW="160px">Dotted Line Manager:</TextLabel>
                  <RadioSelect
                    containerProps={{ mt: '0' }}
                    optionsProps={{ minW: 'auto' }}
                    options={[{ name: 'Yes' }, { name: 'No' }]}
                    selectedIndex={isAutoAcceptDlmNomination ? 0 : 1}
                    onChange={(index) => onChangePropertyHandler(index === 0, 'isAutoAcceptDlmNomination')}
                  />
                </Flex>
              )}
            </Flex>
          )}

          {hasPeerForm && (
            <Box>
              <TextBodyMedium mr={2} mb="2px">
                Set the range for peer nominations (minimum to maximum):
              </TextBodyMedium>
              <Flex alignItems="center" justifyContent="flex-start" pl={3}>
                <TextLabel mr={2}>Min</TextLabel>
                <NumberInput
                  value={minPeers}
                  onChange={(valueAsString, valueAsNumber) => onChangePropertyHandler(valueAsNumber, 'minPeers')}
                  textAlign="start"
                  mr={6}
                  w="115px"
                  type="number"
                  min={0}
                  max={MAX_ALLOWED_NOMINATION_COUNT}
                  inputMode="numeric"
                  format={(val) => (Number.isNaN(val) ? '' : val)}
                  isInvalid={!(minPeers <= maxPeers)}
                />
                <TextLabel mr={2}>Max</TextLabel>
                <NumberInput
                  value={maxPeers}
                  onChange={(valueAsString, valueAsNumber) => onChangePropertyHandler(valueAsNumber, 'maxPeers')}
                  textAlign="start"
                  w="115px"
                  type="number"
                  min={0}
                  max={MAX_ALLOWED_NOMINATION_COUNT}
                  step={1}
                  inputMode="numeric"
                  format={(val) => (Number.isNaN(val) ? '' : val)}
                  isInvalid={!(minPeers <= maxPeers)}
                />
              </Flex>
              {!isValidTotalMinMax && (
                <AlertBar text="Please enter valid min and max peer nomination count" mb={2} mt={3} />
              )}
            </Box>
          )}
        </Flex>
      </Flex>
    </ReviewCreationSidepanel>
  );
};

export { FormsSettingsPanel };
